import Link from 'next/link';
import styled from 'styled-components';

import { ContentsCard } from '~/_legacy/components/molecules/card/contents';
import { CardPresentation } from '~/_legacy/components/molecules/card/presentation';
import { CardSocialIssue } from '~/_legacy/components/molecules/card/socialIssue';
import { Loading } from '~/_legacy/components/molecules/loading';
import { HEADER_HEIGHT_SP } from '~/_legacy/components/organisms/header/_constants';

import { useSWRV1CompetitionById } from '~/api/v1/competitions/[id]/get';
import Layout from '~/pages/_layout.page';
import { CompetitionT, competitionStatus, competitionTitle } from '~/state/competition';
import { PresentationT } from '~/state/presentation';
import { Futura, Tsukushi } from '~/styles';
import { media } from '~/utils';

type Props = {
  id: string;
};

export function Template({ id }: Props) {
  const { competitionLegacyType: competition } = useSWRV1CompetitionById(id);

  if (!competition) {
    return (
      <Layout pageTitle='わくわく寄付コンペ'>
        <Loading />
      </Layout>
    );
  }

  const { corporate, social_issue } = competition;
  const presentations = sortedPresentations(competition);

  const type = social_issue.type === 'GeneralSocialIssue' ? '社会課題' : '地域課題';

  return (
    <Layout pageTitle='わくわく寄付コンペ'>
      {competition.status === 'open' && (
        <ButtonWrapper>
          <Button target='_blank' href='https://forms.gle/2fGWMoY2uosWp6Ht8'>
            SPOの方
            <p>応募する</p>
          </Button>
        </ButtonWrapper>
      )}
      <Top>
        <StyledCoverImage src={competition.cover} alt='cover' loading='lazy' />
      </Top>
      <Main>
        <Section bg={'#fff'}>
          <Labels>
            <Label>わくわく寄付</Label>
            <CorporateLink href={`/corps/${corporate.uid}`}>{corporate.name}</CorporateLink>
          </Labels>
          <Title>{competitionTitle(competition)}</Title>
          <Tags>
            <TagsLeft>
              <Status>{competitionStatus(competition.status)}</Status>
              <Tag>#SPO募集</Tag>
              <Tag>#応募者募集</Tag>
            </TagsLeft>
            {competition.status != 'open' && <Finished>※ 団体の募集は終了いたしました。</Finished>}
          </Tags>
        </Section>
        <Section bg={'#fff'}>
          <Presentations>
            <PresentationTitle>
              <Futura>PRESENTATIONS</Futura>
              <span>
                <p>プレゼンテーション一覧</p>
                {competition.status === 'open' ? (
                  <p>{competition.schedules[1]} 公開 & 投票スタート</p>
                ) : competition.status === 'voting' ? (
                  <p>社員投票受付中 {competition.schedules[2]} まで</p>
                ) : null}
              </span>
            </PresentationTitle>
            <Boxes>
              <StyledCardList>
                {presentations.length > 0 ? (
                  presentations.map((presentation, index) => (
                    <StyledCardListItem key={index}>
                      <CardPresentation
                        presentation={presentation}
                        competitionId={competition.id}
                        isShowVote={competition.vote_count_visible}
                      />
                      {competition.status === 'finished' && index === 0 && (
                        <MostVoted>
                          <MostVotedText>MOST VOTED</MostVotedText>
                        </MostVoted>
                      )}
                    </StyledCardListItem>
                  ))
                ) : (
                  <>
                    <StyledCardListItem>
                      <ContentsCard type='presentation' />
                    </StyledCardListItem>
                    <StyledCardListItem>
                      <ContentsCard type='presentation' />
                    </StyledCardListItem>
                    <StyledCardListItem>
                      <ContentsCard type='presentation' />
                    </StyledCardListItem>
                    <StyledCardListItem>
                      <ContentsCard type='presentation' />
                    </StyledCardListItem>
                  </>
                )}
              </StyledCardList>
            </Boxes>
          </Presentations>
        </Section>
        <ThemeSection bg={'#DEDEDE'}>
          <SectionTitle>
            <Futura>THEME</Futura>
            <h2>募集テーマ</h2>
          </SectionTitle>
          <ThemeTitle>
            本募集テーマに関して、明確な{type}
            を特定し、その解決に向けて取り組んでいる組織・団体を募集します。
          </ThemeTitle>
          <Theme>
            <ThemeLeft>
              <ThemeNameLabel>テーマ:</ThemeNameLabel>
              <ThemeName>
                <Tsukushi>{social_issue.name}</Tsukushi>
              </ThemeName>
              <ThemeDescription>{social_issue.description}</ThemeDescription>
            </ThemeLeft>
            <ThemeRight>
              <CardSocialIssue socialIssue={social_issue} />
            </ThemeRight>
          </Theme>
        </ThemeSection>
        <Section bg={'#fff'}>
          <SectionTitle>
            <Futura>REQUIREMENTS</Futura>
            <h2>応募条件</h2>
          </SectionTitle>
          <Requirements>
            - 対象法人：特定非営利活動法人（認定・非認定問わず）
            <br />
            - 活動地域：日本国内
            <br />
            - 書類審査に必要な資料を期限内にサステナNetにアップロードが完了していること。
            <br />
            - ピッチコンペに参加可能であること。
            <br />- 寄付が決定した後、サステナNet上で寄付企業との継続的対話が可能であること。
          </Requirements>
        </Section>
        <Section bg={'#DEDEDE'}>
          <SectionTitle>
            <Futura>DONATION AMOUNT</Futura>
            <h2>寄付金額</h2>
          </SectionTitle>
          <Amount>
            <Tsukushi>{competition.amount}</Tsukushi>
            <p>万円</p>
          </Amount>
        </Section>
        <Section bg={'#fff'}>
          <SectionTitle>
            <Futura>SCHEDULE</Futura>
            <h2>スケジュール</h2>
          </SectionTitle>
          <Schedules>
            <Schedule>
              <ScheduleDate>{competition.schedules[0]}</ScheduleDate>
              <ScheduleContent>
                1次審査
                <br />
                登録・応募
              </ScheduleContent>
            </Schedule>
            <ScheduleTriangle />
            <Schedule>
              <ScheduleDate>~ {competition.schedules[1]}</ScheduleDate>
              <ScheduleContent>
                ピッチコンペ
                <br />
                動画投稿
              </ScheduleContent>
            </Schedule>
            <ScheduleTriangle />
            <Schedule>
              <ScheduleDate>{competition.schedules[1]} ~</ScheduleDate>
              <ScheduleContentBlack>社員投票</ScheduleContentBlack>
            </Schedule>
            <ScheduleTriangle />
            <Schedule>
              <ScheduleDate>{competition.schedules[2]}</ScheduleDate>
              <ScheduleContentSquare>
                投票結果
                <br />
                発表
              </ScheduleContentSquare>
            </Schedule>
          </Schedules>
        </Section>
      </Main>
    </Layout>
  );
}

function sortedPresentations(competition: CompetitionT): PresentationT[] {
  const presentation = competition.presentations;
  if (competition.status === 'finished') {
    if (competition.vote_count_visible) {
      return presentation.sort((a, b) => b.votes - a.votes);
    } else {
      return presentation.sort((a) =>
        a.organization.id === competition.most_voted_organization?.id ? -1 : 1,
      );
    }
  } else {
    return presentation;
  }
}

const ButtonWrapper = styled.div`
  position: fixed;
  right: 50px;
  bottom: 50px;
  z-index: 100;

  ${media.mid`
    right: 20px;
    bottom: 20px;
  `}
`;

const Button = styled.a`
  width: 128px;
  height: 128px;
  border-radius: 64px;
  color: #fff;
  background: linear-gradient(-45deg, #ff1f73, #ffae56);
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  line-height: 26px;
  letter-spacing: 1px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.26);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > p {
    font-size: 22px;
  }

  ${media.mid`
    width: 110px;
    height: 110px;
    border-radius: 55px;
    font-size: 14px;
    line-height: 24px;

    & > p {
      font-size: 20px;
    }
  `}
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #dedede;
`;

const StyledCoverImage = styled.img`
  width: min(1000px, 100%);
  aspect-ratio: 2 / 1;
  object-fit: cover;

  ${media.sp`
    width: 100%;
    max-height: 400px;
  `}
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Section = styled.div<{ bg: string }>`
  width: 100%;
  padding: 100px 0;
  background: ${(props) => props.bg};
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.mid`
    padding: 40px 20px;
  `}
`;

const Labels = styled.div`
  width: 100%;
  max-width: 830px;
  display: flex;
  flex-direction: row;
  align-items: center;

  ${media.mid`
    padding: 0 20px;
  `}
`;

const Label = styled.div`
  background: #ceab19;
  padding: 6px 14px;
  font-size: 12px;
  color: #fff;
  font-weight: bold;
`;

const CorporateLink = styled(Link)`
  margin-left: 10px;
  text-decoration: underline;
  font-size: 12px;
  font-weight: bold;
`;

const Title = styled.div`
  margin-top: 25px;
  padding-bottom: 70px;
  width: 100%;
  max-width: 830px;
  font-size: 30px;
  font-weight: bold;
  color: #070707;
  line-height: 50px;
  letter-spacing: 1px;
  border-bottom: 1px solid #b2b2b2;

  ${media.mid`
    width: 100%;
    font-size: 25px;
    line-height: 35px;
    padding-bottom: 30px;
  `}
`;

const Tags = styled.div`
  margin-top: 15px;
  width: 100%;
  max-width: 830px;
  display: flex;
  flex-direction: row;
  align-items: center;

  ${media.mid`
    padding: 0 20px;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  `}
`;

const TagsLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Status = styled.div`
  margin-right: 10px;
  width: 85px;
  height: 85px;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 2px;
  background: #000;
  color: #fff;
  text-align: center;
  line-height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: pre;

  ${media.mid`
    width: 80px;
    height: 80px;
  `}
`;

const Tag = styled.div`
  margin-left: 15px;
  color: #747474;
  font-size: 12px;
`;

const Finished = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: bold;
  color: #ff0000;
  text-align: right;

  ${media.mid`
    margin-top: 20px;
    margin-bottom: 20px;
  `}
`;

const SectionTitle = styled.div`
  text-align: center;
  color: #ceab19;
  font-weight: bold;
  font-size: 23px;
  letter-spacing: 1px;

  & > h2 {
    margin-top: 15px;
    color: #000;
    font-size: 28px;
  }

  ${media.mid`
    font-size: 20px;

    & > h2 {
      font-size: 25px;
    }
  `}
`;

const ThemeTitle = styled.div`
  margin-top: 70px;
  padding-bottom: 70px;
  border-bottom: 1px solid #b2b2b2;
  width: 100%;
  max-width: 830px;
  color: #070707;
  font-size: 30px;
  font-weight: bold;
  line-height: 50px;

  ${media.mid`
    margin: 40px 20px 0;
    padding-bottom: 40px;
    width: 100%;
    font-size: 20px;
    line-height: 34px;
  `}
`;

const Theme = styled.div`
  margin-top: 40px;
  width: 100%;
  max-width: 830px;
  display: flex;
  flex-direction: row;
  justify-content: center;

  ${media.mid`
    padding: 0 20px;
    width: 100%;
    flex-direction: column;
  `}
`;

const ThemeLeft = styled.div`
  flex: 1;
`;

const ThemeNameLabel = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const ThemeName = styled.div`
  margin-top: 15px;
  font-size: 46px;
  font-weight: bold;
  letter-spacing: 4px;
`;

const ThemeDescription = styled.div`
  margin-top: 15px;
  font-size: 18px;
  line-height: 35px;
  letter-spacing: 1px;
  text-align: justify;

  ${media.mid`
    margin-top: 30px;
    line-height: 30px;
  `}
`;

const ThemeRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 100px;
  > * {
    max-width: none;
    width: 300px;
    height: 384px;
  }

  ${media.mid`
    margin-top: 40px;
    justify-content: center;
  `}
`;

const Requirements = styled.div`
  margin-top: 70px;
  width: 100%;
  max-width: 830px;
  font-size: 20px;
  line-height: 40px;
  letter-spacing: 1px;

  ${media.mid`
    margin-top: 40px;
    padding: 0 20px;
    font-size: 16px;
    line-height: 30px;
  `}
`;

const Amount = styled.div`
  margin-top: 70px;
  width: 100%;
  max-width: 830px;
  font-size: 108px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  & > p {
    margin-left: 5px;
    font-size: 33px;
    font-weight: normal;
    align-self: flex-end;
  }

  ${media.mid`
    margin-top: 40px;
    font-size: 80px;

    & > p {
      font-size: 24px;
    }
  `}
`;

const Schedules = styled.div`
  margin-top: 70px;
  width: 100%;
  max-width: 830px;
  display: flex;
  flex-direction: row;
  align-items: center;

  ${media.mid`
    margin-top: 40px;
    flex-direction: column;
  `}
`;

const Schedule = styled.div`
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:first-child {
    margin-left: 0;
  }

  ${media.mid`
    margin-left: 0;
    margin-top: 20px;

    &:first-child {
      margin-top: 0;
    }
  `}
`;

const ScheduleDate = styled.div`
  font-size: 19px;
`;

const ScheduleContent = styled.div`
  margin-top: 22px;
  width: 200px;
  height: 200px;
  border-radius: 105px;
  background: #562981;
  font-size: 26px;
  color: #fff;
  text-align: center;
  line-height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${media.mid`
    width: 150px;
    height: 150px;
    font-size: 20px;
  `}
`;

const ScheduleContentBlack = styled(ScheduleContent)`
  background: #fff;
  border: 3px solid #000;
  color: #000;

  ${media.mid`
    width: 150px;
    height: 150px;
    font-size: 20px;
  `}
`;

const ScheduleContentSquare = styled(ScheduleContent)`
  border-radius: 0;

  ${media.mid`
    width: 150px;
    height: 150px;
    font-size: 20px;
  `}
`;

const ScheduleTriangle = styled.div`
  margin-left: 10px;
  position: relative;
  top: 20px;
  border-top: 20px solid transparent;
  border-bottom: 20px solid transparent;
  border-left: 30px solid #000;

  ${media.mid`
    margin-top: 5px;
    margin-left: 0;
    border-top: 30px solid #000;
    border-right: 20px solid transparent;
    border-left: 20px solid transparent;
  `}
`;

const Presentations = styled.div`
  margin-top: -150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${media.mid`
    margin-top: -100px;
  `}
`;

const PresentationTitle = styled.div`
  margin-top: 40px;
  width: 100%;
  max-width: 830px;
  letter-spacing: 2px;
  display: flex;
  flex-direction: row;
  align-items: center;

  & > i {
    font-style: italic;
    font-size: 40px;
    font-weight: bold;
  }

  & > span {
    font-size: 12px;
    margin-left: 10px;
    font-weight: bold;
    white-space: pre;
    line-height: 16px;
  }

  ${media.mid`
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    & > i {
      font-size: 35px;
    }

    & > span {
      display: block;
      margin-top: 8px;
      margin-left: 0;
      font-size: 14px;
    }
  `}
`;

const Boxes = styled.div`
  margin-top: 30px;
  margin-bottom: 60px;
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  padding-left: calc(50% - 500px);
  width: calc(50% + 500px);
  padding-bottom: 42px;

  ${media.mid`
    margin-top: 50px;
    padding-left: 20px;
    width: calc(100% - 40px);
  `}
`;

const ThemeSection = styled(Section)`
  margin-top: -400px;
  padding-top: 400px;

  ${media.mid`
    margin-top: -280px;
    padding-top: 230px;
  `}
`;

const StyledCardList = styled.ul`
  display: flex;
  gap: 20px;
  list-style: none;
  width: 100%;
`;

const StyledCardListItem = styled.li`
  position: relative;
  width: 320px;
  z-index: 0;
  flex-shrink: 0;

  ${media.mid`
    width: 100%;
  `}
`;

const MostVoted = styled.div`
  position: absolute;
  bottom: -40px;
  left: 12px;
  width: 320px;
  height: 100%;
  background: #262626;
  z-index: -100;
`;

const MostVotedText = styled(Futura)`
  position: absolute;
  bottom: 10px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  width: 320px;
  color: #fff;
`;
